import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Tooltip, Typography } from "@mui/material";

const OverflowListSummary = ({ items }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const containerRef = useRef(null);
  const measurementRef = useRef(null);
  const fullText = items.join(", ");

  useEffect(() => {
    const updateOverflow = () => {
      setIsOverflowing(
        (measurementRef.current?.scrollWidth || 0) >
          (containerRef.current?.clientWidth || 0),
      );
    };

    updateOverflow();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateOverflow);
      return () => window.removeEventListener("resize", updateOverflow);
    }

    const resizeObserver = new ResizeObserver(updateOverflow);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [fullText]);

  if (!items.length) {
    return <Typography component="span">-</Typography>;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: expanded ? "flex-start" : "center",
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        ref={measurementRef}
        aria-hidden="true"
        sx={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          fontSize: "14px",
          pointerEvents: "none",
        }}
      >
        {fullText}
      </Box>
      <Tooltip title={!isOverflowing ? fullText : ""} arrow>
        <Typography
          component="span"
          variant="body2"
          noWrap={!expanded}
          sx={{
            minWidth: 0,
            overflow: expanded ? "visible" : "hidden",
            overflowWrap: expanded ? "anywhere" : "normal",
            textOverflow: expanded ? "clip" : "ellipsis",
            whiteSpace: expanded ? "normal" : "nowrap",
          }}
        >
          {fullText}
        </Typography>
      </Tooltip>

      {isOverflowing && (
        <Button
          size="small"
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
          sx={{
            flexShrink: 0,
            minWidth: "auto",
            minHeight: 20,
            ml: "6px",
            p: "1px 4px",
            color: "#154D8F",
            fontSize: "12px",
            lineHeight: "20px",
            textTransform: "none",
          }}
        >
          {expanded ? "Less" : "... More"}
        </Button>
      )}
    </Box>
  );
};

OverflowListSummary.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default OverflowListSummary;
